import React, { useCallback, useEffect, useState } from 'react';
import './Supporter.css';

const CHEERS = [
  ['great_walk', 'Great walk! 👏'],
  ['lets_go', 'Let’s go 💪'],
  ['keep_going', 'Keep it up 🔥'],
  ['catch_up', 'Catch up today?'],
  ['hello', 'Hello 👋'],
];

function configuration() {
  return {
    url: process.env.REACT_APP_SUPABASE_URL?.replace(/\/$/, ''),
    key: process.env.REACT_APP_SUPABASE_ANON_KEY,
  };
}

async function rpc(name, body, signal) {
  const { url, key } = configuration();
  if (!url || !key) throw new Error('Supporter links are not configured');

  const response = await fetch(`${url}/rest/v1/rpc/${name}`, {
    method: 'POST',
    headers: {
      apikey: key,
      Authorization: `Bearer ${key}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    signal,
  });
  const json = await response.json().catch(() => null);
  if (!response.ok) throw new Error(json?.message || 'Request failed');
  return json;
}

function number(value) {
  return Number(value || 0).toLocaleString('en-US');
}

function dayName(date) {
  return new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short' }).slice(0, 2);
}

function SupporterDay({ day }) {
  const labels = {
    hit: 'target reached',
    missed: 'target not reached',
    no_data: 'no data, protected',
    today: 'today, in progress',
    none: 'no plan',
  };
  const symbol =
    day.status === 'hit' ? '✓' : day.status === 'missed' ? '–' : day.status === 'today' ? '•' : '';

  return (
    <div className="supporter-day" aria-label={`${dayName(day.date)}, ${labels[day.status] || ''}`}>
      {dayName(day.date)}
      <div className={`supporter-dot ${day.status}`}>{symbol}</div>
    </div>
  );
}

function LoadingView() {
  return (
    <div className="supporter-skeleton" role="status" aria-label="Loading supporter progress">
      <span className="supporter-visually-hidden">Loading supporter progress</span>
      <div className="supporter-skeleton-line supporter-skeleton-title" />
      <div className="supporter-skeleton-line supporter-skeleton-subtitle" />
      <div className="supporter-skeleton-card">
        <div className="supporter-skeleton-line supporter-skeleton-label" />
        <div className="supporter-skeleton-line supporter-skeleton-count" />
        <div className="supporter-skeleton-line supporter-skeleton-bar" />
        <div className="supporter-skeleton-line supporter-skeleton-meta" />
      </div>
      <div className="supporter-skeleton-line supporter-skeleton-heading" />
      <div className="supporter-skeleton-week" aria-hidden="true">
        {Array.from({ length: 7 }, (_, index) => (
          <div className="supporter-skeleton-day" key={index}>
            <div className="supporter-skeleton-line supporter-skeleton-day-label" />
            <div className="supporter-skeleton-dot" />
          </div>
        ))}
      </div>
      <div className="supporter-skeleton-line supporter-skeleton-heading" />
      <div className="supporter-skeleton-actions" aria-hidden="true">
        <div className="supporter-skeleton-pill" />
        <div className="supporter-skeleton-pill" />
        <div className="supporter-skeleton-pill supporter-skeleton-pill-short" />
      </div>
    </div>
  );
}

function ReadyView({ data, onCheer, sending }) {
  const unit = data.unit === 'pushes' ? 'pushes' : 'steps';
  const progress = data.target ? Math.min(100, Math.round((data.steps / data.target) * 100)) : 0;
  const meta = [];
  if (data.target && data.steps >= data.target) meta.push('Today’s target reached 🎉');
  else if (data.target) meta.push(`${number(data.target - data.steps)} to go`);
  if (data.streak > 1) meta.push(`${data.streak}-day streak`);
  if (data.synced_at) {
    meta.push(
      `updated ${new Date(data.synced_at).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      })}`,
    );
  }

  return (
    <>
      <h1>How {data.name} is doing today</h1>
      <p>
        {data.name} shared this with you ({data.label}).
      </p>

      <section className="supporter-card" aria-label="Today’s walking progress">
        <div className="supporter-label">TODAY</div>
        <div className="supporter-count">
          {number(data.steps)}{' '}
          <span>
            {data.target ? `of ${number(data.target)} ${unit}` : unit}
          </span>
        </div>
        <div
          className="supporter-bar"
          role="progressbar"
          aria-label="Today’s target progress"
          aria-valuemin="0"
          aria-valuemax={data.target || 0}
          aria-valuenow={data.target ? Math.min(data.steps, data.target) : 0}
        >
          <div className="supporter-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="supporter-meta">{meta.join(' · ')}</div>
      </section>

      <h2>This week</h2>
      <div className="supporter-week">
        {(data.week || []).map((day) => (
          <SupporterDay day={day} key={day.date} />
        ))}
      </div>

      <h2>Send {data.name} a cheer</h2>
      {data.cheered_today ? (
        <div className="supporter-sent" role="status">
          Cheer sent today ✓ {data.name} gets it as a notification.
        </div>
      ) : (
        <div className="supporter-cheers">
          {CHEERS.map(([value, label]) => (
            <button
              className="supporter-cheer"
              disabled={sending}
              key={value}
              onClick={() => onCheer(value)}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <p className="supporter-small">
        Only people with this link can see this page. {data.name} can turn it off at any time in
        Walk It Off. Cheers are short preset messages.
      </p>
    </>
  );
}

export default function Supporter({ token }) {
  const [state, setState] = useState({ status: 'loading', data: null });
  const [sending, setSending] = useState(false);

  const load = useCallback(
    async (signal) => {
      if (!token) {
        setState({ status: 'inactive', data: null });
        return;
      }
      setState({ status: 'loading', data: null });
      try {
        const data = await rpc('supporter_view', { p_token: token }, signal);
        setState({ status: data ? 'ready' : 'inactive', data: data || null });
      } catch (error) {
        if (error.name !== 'AbortError') setState({ status: 'failed', data: null });
      }
    },
    [token],
  );

  useEffect(() => {
    document.title = 'Walking today · Walk It Off';
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex, nofollow');
    window.scrollTo(0, 0);

    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    if (state.status === 'ready') document.title = `${state.data.name} today · Walk It Off`;
  }, [state]);

  const sendCheer = async (message) => {
    setSending(true);
    try {
      await rpc('supporter_cheer', { p_token: token, p_message: message });
      setState((current) => ({
        ...current,
        data: { ...current.data, cheered_today: true },
      }));
    } catch {
      window.alert('The cheer wasn’t sent. Try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="supporter-page">
      <a className="supporter-brand" href="/" aria-label="Walk It Off home">
        WALK IT OFF
      </a>

      {state.status === 'loading' ? <LoadingView /> : null}
      {state.status === 'inactive' ? (
        <>
          <h1>This link isn’t active</h1>
          <p>It may have been turned off. Ask the person who shared it for a new one.</p>
        </>
      ) : null}
      {state.status === 'failed' ? (
        <>
          <h1>We couldn’t load this</h1>
          <p>Check your connection and try again.</p>
          <button className="supporter-retry" onClick={() => load()} type="button">
            Try again
          </button>
        </>
      ) : null}
      {state.status === 'ready' ? (
        <ReadyView data={state.data} onCheer={sendCheer} sending={sending} />
      ) : null}
    </main>
  );
}
